import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Edit3, X, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DownloadEntryButton, PrintEntryButton } from "@/components/PdfExporter";
import { ReflectionButton } from "@/components/JournalCoach";
import { MOOD_EMOJIS } from "@/utils/constants";
import { API } from "@/utils/api";
import DOMPurify from "dompurify";
import { useFeatures, FeatureGate } from "@/context/FeaturesContext";
import { PricingModal } from "@/components/UpgradeBanner";

// Animated gradient glow for UnCloned mood
const UnClonedMoodIcon = ({ size = "lg" }) => {
  const sizeClass = size === "lg" ? "w-10 h-10" : "w-8 h-8";
  return (
    <>
      <style>{`
        @keyframes uncloned-gradient-modal {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .uncloned-glow-modal {
          animation: uncloned-gradient-modal 3s ease infinite;
        }
      `}</style>
      <div 
        className={`${sizeClass} uncloned-glow-modal`}
        style={{
          background: 'linear-gradient(90deg, var(--theme-primary) 0%, var(--theme-secondary) 25%, #D0FF0B 50%, #0FFFE2 75%, var(--theme-primary) 100%)',
          backgroundSize: '200% 100%',
          WebkitMaskImage: 'url("/mood-uncloned.png")',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: 'url("/mood-uncloned.png")',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          filter: 'drop-shadow(0 0 8px rgba(var(--theme-primary-rgb), 0.5)) drop-shadow(0 0 16px rgba(var(--theme-secondary-rgb), 0.3))',
        }}
      />
    </>
  );
};

// Helper to render mood emoji or custom image
const MoodDisplay = ({ moodData, size = "lg" }) => {
  if (!moodData) return <span className="text-2xl sm:text-3xl">✨</span>;
  
  // If custom mood has an image URL, show the image
  if (moodData.imageUrl) {
    return (
      <img 
        src={moodData.imageUrl} 
        alt={moodData.label || 'Custom Mood'} 
        className={size === "lg" ? "w-10 h-10 object-contain" : "w-8 h-8 object-contain"}
      />
    );
  }
  
  // Show animated gradient for uncloned/custom_branded mood without custom image
  if (moodData.isCustom || moodData.value === 'uncloned' || moodData.value === 'custom_branded') {
    return <UnClonedMoodIcon size={size} />;
  }
  
  return <span className="text-2xl sm:text-3xl">{moodData.emoji}</span>;
};

const ViewEntryModal = ({ entry, onClose, onEdit, onRefresh, clientBranding = null }) => {
  const [currentEntry, setCurrentEntry] = useState(entry);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const { isFeatureEnabled, customMood } = useFeatures();
  
  // Get mood data - use custom mood if entry.mood is "uncloned" or "custom_branded" and custom mood is configured
  let moodData = MOOD_EMOJIS.find((m) => m.value === currentEntry?.mood);
  if ((currentEntry?.mood === 'uncloned' || currentEntry?.mood === 'custom_branded') && customMood?.enabled) {
    moodData = {
      value: currentEntry?.mood,
      label: customMood.label || 'Custom',
      emoji: customMood.imageUrl ? null : '✨',
      imageUrl: customMood.imageUrl
    };
  }
  
  // Update local state when entry prop changes
  useEffect(() => {
    setCurrentEntry(entry);
  }, [entry]);
  
  if (!currentEntry) return null;
  
  const handleReflectionSaved = (reflection) => {
    // Update local entry state with the new reflection
    setCurrentEntry(prev => ({
      ...prev,
      ai_reflection: reflection,
      ai_reflection_date: new Date().toISOString()
    }));
    // Also trigger a refresh of the entries list
    if (onRefresh) {
      onRefresh();
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
      onClick={onClose}
      data-testid="view-entry-modal"
    >
      <div 
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          backgroundColor: 'var(--theme-background)',
          boxShadow: '0 25px 50px -12px rgba(var(--theme-primary-rgb), 0.3)',
          border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="sticky top-0 flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 z-10 gap-3"
          style={{
            backgroundColor: 'var(--theme-background)',
            borderBottom: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)'
          }}
        >
          <div className="flex items-center gap-3">
            <MoodDisplay moodData={moodData} size="lg" />
            <div>
              <span className="date-badge text-xs sm:text-sm">{currentEntry.date}</span>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)' }}>{moodData?.label || "Feeling"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {/* Print Button - Feature gated */}
            <FeatureGate 
              feature="export_pdf" 
              mode="prompt" 
              onUpgrade={() => setShowPricingModal(true)}
            >
              <PrintEntryButton entry={currentEntry} clientBranding={clientBranding} customMood={customMood} />
            </FeatureGate>
            {/* Download PDF Button - Feature gated */}
            <FeatureGate 
              feature="export_pdf" 
              mode="prompt" 
              onUpgrade={() => setShowPricingModal(true)}
            >
              <DownloadEntryButton entry={currentEntry} variant="icon" clientBranding={clientBranding} />
            </FeatureGate>
            <Button
              onClick={() => { onClose(); onEdit(currentEntry); }}
              className="btn-primary text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              data-testid="edit-from-view-btn"
            >
              <Edit3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Edit
            </Button>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 rounded-full hover:bg-[var(--theme-background-secondary)] transition-colors"
              data-testid="close-view-btn"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--theme-primary)]" />
            </button>
          </div>
        </div>
        
        {/* Entry Content */}
        <div className="p-6">
          {currentEntry.title && (
            <h2 className="text-3xl font-black gradient-text text-center mb-6">{currentEntry.title}</h2>
          )}
          
          <div 
            className="entry-rich-content leading-relaxed text-lg"
            style={{ color: 'var(--theme-text)' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentEntry.content || '') }}
          />
          
          {currentEntry.images && currentEntry.images.length > 0 && (
            <div className="image-gallery mt-6">
              {currentEntry.images.map((imageId) => (
                <img
                  key={imageId}
                  src={`${API}/images/${imageId}`}
                  alt="Journal"
                  className="gallery-image rounded-lg"
                />
              ))}
            </div>
          )}
          
          {/* AI Journal Coach - Get Reflection Button (Feature gated) */}
          {isFeatureEnabled('ai_coach') && (
            <div 
              className="mt-8 pt-6"
              style={{ borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}
            >
              {/* Show saved reflection if exists */}
              {currentEntry.ai_reflection && (
                <div 
                  className="mb-6 rounded-2xl p-6"
                  style={{
                    background: `linear-gradient(to right, var(--theme-background-secondary), rgba(var(--theme-primary-rgb), 0.1))`,
                    border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)'
                  }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Brain className="w-5 h-5 text-[var(--theme-primary)]" />
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>AI Reflection</h3>
                    {currentEntry.ai_reflection_date && (
                      <span className="text-xs ml-auto" style={{ color: 'var(--theme-text-secondary)' }}>
                        {new Date(currentEntry.ai_reflection_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="prose prose-invert prose-pink max-w-none">
                    {currentEntry.ai_reflection.split('\n\n').map((paragraph, i) => (
                      <p key={`reflection-${i}-${paragraph.slice(0, 20)}`} className="leading-relaxed mb-3 last:mb-0 text-sm" style={{ color: 'var(--theme-text)', opacity: 0.8 }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-center mb-4">
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  {currentEntry.ai_reflection ? "Generate a new reflection?" : "Want deeper insight into this entry?"}
                </p>
              </div>
              <div className="flex justify-center">
                <ReflectionButton 
                  entryId={currentEntry.id} 
                  mood={currentEntry.mood} 
                  onReflectionSaved={handleReflectionSaved}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Pricing Modal for upgrade */}
      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
    </div>,
    document.body
  );
};

export default ViewEntryModal;
